import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getVendorBooth, purchaseItem, getBlueprints, createOffer, createSubscriptionCheckout } from '../services/api';

function BoothBadges({ vendorId }) {
  const [badges, setBadges] = React.useState([]);

  React.useEffect(() => {
    getBlueprints().then(data => {
      const bps = (data.blueprints || []).filter(b => b.creatorId === vendorId);
      const originals = bps.filter(b => !b.forkedFrom);
      const b = [];
      if (originals.length > 0) b.push("📐 Blueprint Author");
      if (bps.some(bp => bp.forkedFrom)) b.push("🔀 Contributor");
      if (originals.some(bp => parseInt(bp.starCount || 0) >= 10)) b.push("⭐ Popular Plan");
      if (originals.some(bp => parseInt(bp.variationCount || 0) >= 5)) b.push("🌳 Tree Grower");
      if (originals.length >= 5) b.push("📚 Prolific Author");
      setBadges(b);
    }).catch(() => {});
  }, [vendorId]);

  if (badges.length === 0) return null;

  return (
    <div className="booth-badges">
      {badges.map((b, i) => <span className="contrib-badge" key={i}>{b}</span>)}
    </div>
  );
}
import { useCustomerAuth } from '../services/CustomerAuthContext';
import { useAuth } from '../services/AuthContext';

function ItemDescription({ text }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!text) return null;
  if (text.length <= 255) return <p className="booth-item-desc">{text}</p>;
  return (
    <p className="booth-item-desc">
      {expanded ? text : text.slice(0, 255) + '...'}
      <span className="see-more" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
        {expanded ? ' see less' : ' see more'}
      </span>
    </p>
  );
}

function LightboxActions({ item, vendorId }) {
  const [mode, setMode] = React.useState(null); // null, 'buy', 'offer'
  const [tip, setTip] = React.useState(0);
  const [offerAmount, setOfferAmount] = React.useState('');
  const [offerSent, setOfferSent] = React.useState(false);

  const price = item.price;
  const tipPresets = [0, 500, 1000, 2000, 5000]; // $0, $5, $10, $20, $50
  const [customTip, setCustomTip] = React.useState('');
  const total = price + tip;

  function handleTipPreset(t) {
    setTip(t);
    setCustomTip('');
  }

  function handleCustomTip(val) {
    setCustomTip(val);
    const cents = Math.round(parseFloat(val) * 100);
    if (!isNaN(cents) && cents >= 0) setTip(cents);
    else setTip(0);
  }

  async function handleBuy() {
    try {
      const data = await purchaseItem(vendorId, item.itemId, tip);
      window.location.href = data.url;
    } catch (err) { alert(err.error || 'Purchase failed'); }
  }

  async function handleOffer() {
    const cents = Math.round(parseFloat(offerAmount) * 100);
    if (!cents || cents <= 0) { alert('Enter a valid offer amount'); return; }
    try {
      await createOffer({ vendorId, itemId: item.itemId, amount: cents });
      setOfferSent(true);
    } catch (err) { alert(err.error || 'Offer failed'); }
  }

  if (!mode) {
    return (
      <div className="lightbox-actions">
        <button className="btn-primary lightbox-buy" onClick={() => setMode('buy')}>Buy — ${(price / 100).toFixed(2)}</button>
        <button className="btn-secondary lightbox-offer-btn" onClick={() => setMode('offer')}>Make an Offer</button>
      </div>
    );
  }

  if (mode === 'buy') {
    return (
      <div className="lightbox-actions">
        <div className="tip-section">
          <p className="tip-label">Add a tip for the maker?</p>
          <div className="tip-options">
            {tipPresets.map(t => (
              <button key={t} className={`tip-btn ${tip === t && !customTip ? 'active' : ''}`} onClick={() => handleTipPreset(t)}>
                {t === 0 ? 'No tip' : `$${(t / 100).toFixed(0)}`}
              </button>
            ))}
          </div>
          <div className="tip-custom">
            <input type="number" step="0.01" min="0" placeholder="Custom amount" value={customTip} onChange={e => handleCustomTip(e.target.value)} className="tip-custom-input" />
          </div>
          {tip > 0 && <p className="tip-amount">Tip: ${(tip / 100).toFixed(2)}</p>}
        </div>
        <div className="tip-total">Total: ${(total / 100).toFixed(2)}</div>
        <button className="btn-primary lightbox-buy" onClick={handleBuy}>Complete Purchase</button>
        <button className="btn-secondary" onClick={() => { setMode(null); setTip(0); }} style={{marginTop:'0.5rem'}}>← Back</button>
      </div>
    );
  }

  if (mode === 'offer') {
    if (offerSent) {
      return (
        <div className="lightbox-actions">
          <p className="offer-sent">✓ Offer sent! The vendor will review it.</p>
          <button className="btn-secondary" onClick={() => { setMode(null); setOfferSent(false); }}>← Back</button>
        </div>
      );
    }
    return (
      <div className="lightbox-actions">
        <p className="offer-label">Your offer (USD):</p>
        <input type="number" step="0.01" placeholder={`List price: $${(price / 100).toFixed(2)}`} value={offerAmount} onChange={e => setOfferAmount(e.target.value)} className="offer-input" />
        <button className="btn-primary" onClick={handleOffer} disabled={!offerAmount || parseFloat(offerAmount) <= 0}>Send Offer</button>
        <button className="btn-secondary" onClick={() => setMode(null)} style={{marginTop:'0.5rem'}}>← Back</button>
      </div>
    );
  }
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}
import Header from '../components/Header';

function VendorBooth() {
  const { vendorId } = useParams();
  const { customer, loading: authLoading } = useCustomerAuth();
  const { vendor: loggedInVendor } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxItem, setLightboxItem] = useState(null);
  const [freeVisitsUsed, setFreeVisitsUsed] = useState(0);

  const FREE_VISIT_LIMIT = 3;
  const tier = customer?.subscriptionTier || 'explorer';
  const vendorHasAccess = loggedInVendor && loggedInVendor.vendorStatus === 'active';
  const isPaid = tier === 'member' || tier === 'enthusiast' || tier === 'insider';
  const hasVideoAccess = vendorHasAccess || tier === 'enthusiast' || tier === 'insider';

  async function handleUpgrade(t) {
    if (!customer) { window.location.href = '/customer/register'; return; }
    try { const data = await createSubscriptionCheckout(t); window.location.href = data.url; }
    catch (e) { alert(e.error || 'Failed'); }
  }

  // Free tier visit tracking
  const getVisitData = () => {
    try {
      const data = JSON.parse(localStorage.getItem('ts_free_visits') || '{}');
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
      if (data.month !== monthKey) return { month: monthKey, visited: [] };
      return data;
    } catch { return { month: `${new Date().getFullYear()}-${new Date().getMonth()}`, visited: [] }; }
  };

  const recordVisit = () => {
    const data = getVisitData();
    if (!data.visited.includes(vendorId)) {
      data.visited.push(vendorId);
      localStorage.setItem('ts_free_visits', JSON.stringify(data));
    }
    setFreeVisitsUsed(data.visited.length);
  };

  const visitData = getVisitData();
  const alreadyVisited = visitData.visited.includes(vendorId);
  const freeVisitsRemaining = FREE_VISIT_LIMIT - visitData.visited.length;
  const freeHasAccess = alreadyVisited || freeVisitsRemaining > 0;
  const hasAccess = vendorHasAccess || isPaid || (tier === 'explorer' && customer && freeHasAccess);

  useEffect(() => {
    if (hasAccess && customer && tier === 'explorer' && !alreadyVisited) {
      recordVisit();
    }
  }, [hasAccess]);

  useEffect(() => {
    getVendorBooth(vendorId)
      .then(data => {
        setVendor(data.vendor);
        setItems(data.items || []);
      })
      .catch(() => setError('Booth not found'))
      .finally(() => setLoading(false));
  }, [vendorId]);

  if (loading || authLoading) return <div className="booth-page"><Header /><p className="booth-loading">Loading booth...</p></div>;
  if (error) return <div className="booth-page"><Header /><p className="booth-error">{error}</p></div>;

  if (!hasAccess) {
    return (
      <div className="booth-page">
        <Header />
        <div className="workshop-content">
          {/* Vendor Preview Header */}
          <div className="booth-preview-header">
            <div className="booth-preview-logo">
              {vendor?.profileImageUrl ? (
                <img src={vendor.profileImageUrl} alt={vendor.businessName} />
              ) : (
                <div className="booth-logo-placeholder">🪵</div>
              )}
            </div>
            <div>
              <h1>{vendor?.businessName}</h1>
              <p style={{color:'var(--accent)'}}>{vendor?.specialty}</p>
            </div>
          </div>

          {/* Blurred trailer of what's inside */}
          <div className="booth-preview-trailer">
            <div className="booth-trailer-items">
              {items.slice(0, 4).map((item, i) => (
                <div className="booth-trailer-card" key={i}>
                  <div className="booth-trailer-image">
                    {item.images && item.images.length > 0 ? (
                      <img src={item.images[0].url} alt={item.name} />
                    ) : <div className="booth-trailer-placeholder">🔨</div>}
                  </div>
                  <h4>{item.name}</h4>
                  <span className="booth-trailer-price">${(item.price / 100).toFixed(2)}</span>
                </div>
              ))}
              {items.length === 0 && <p className="empty-state">This vendor is setting up their booth...</p>}
            </div>
            <div className="booth-trailer-overlay"></div>
          </div>

          {/* Feature highlights */}
          <div className="workshop-preview-features" style={{marginTop:'2rem'}}>
            <div className="preview-feature">
              <span className="preview-feature-icon">🛒</span>
              <h4>Shop Handmade</h4>
              <p>Buy directly from the maker. No middlemen, no markup.</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">🎥</span>
              <h4>Live Streams</h4>
              <p>Watch artisans work in real-time from their shops.</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">💬</span>
              <h4>Commission Work</h4>
              <p>Request custom pieces built to your exact specifications.</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">🎁</span>
              <h4>Tips & Offers</h4>
              <p>Tip your favorite makers or make an offer on any piece.</p>
            </div>
          </div>

          {/* Paywall CTA */}
          <div className="workshop-paywall-cta">
            <h2>🔒 {customer ? 'Free Visits Used' : 'Membership Required'}</h2>
            <p style={{color:'var(--text-muted)', margin:'0.5rem 0 1.5rem'}}>
              {customer
                ? `You've used all ${FREE_VISIT_LIMIT} free booth visits this month. Upgrade for unlimited access.`
                : 'Become a Member or Insider to visit vendor booths, purchase items, and unlock the full experience.'}
            </p>
            <div style={{display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap'}}>
              <Link to="/#subscribe" className="btn-primary" style={{textDecoration:'none'}}>Member — $4.99/mo</Link>
              <Link to="/#subscribe" className="btn-secondary" style={{textDecoration:'none'}}>Insider — $12.99/mo</Link>
            </div>
            {!customer && <p style={{marginTop:'1rem', fontSize:'0.85rem', color:'var(--text-muted)'}}>Already a member? <Link to="/customer/login" style={{color:'var(--accent)'}}>Sign in</Link></p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booth-page">
      <Header />
      {customer && tier === 'explorer' && (
        <div className="free-visit-counter">
          {FREE_VISIT_LIMIT - freeVisitsUsed} of {FREE_VISIT_LIMIT} free visits remaining this month
        </div>
      )}
      {vendor.liveStreamId && hasVideoAccess && (
        <div className="booth-tv-layout">
          <div className="booth-tv-monitor">
            <div className="tv-bezel">
              <div className="tv-screen">
                <iframe src={`https://www.youtube.com/embed/${vendor.liveStreamId}?autoplay=1&mute=1`} title="Live stream" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
              </div>
              <div className="tv-status-bar">
                <span className="tv-rec">● REC</span>
                <span className="tv-title">{vendor.liveStreamTitle}</span>
              </div>
            </div>
          </div>
          {vendor.recentVideos && vendor.recentVideos.length > 0 && (
            <div className="booth-side-panel">
              <h4 className="side-panel-title">Recent Videos</h4>
              <div className="side-panel-videos">
                {vendor.recentVideos.map((v, i) => (
                  <a href={`https://youtube.com/watch?v=${v.videoId}`} target="_blank" rel="noopener noreferrer" className="side-panel-card" key={i}>
                    <img src={v.thumbnail} alt={v.title} />
                    <span>{v.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!vendor.liveStreamId && vendor.featuredVideoUrl && getYouTubeEmbedUrl(vendor.featuredVideoUrl) && hasVideoAccess && (
        <div className="booth-tv-layout">
          <div className="booth-tv-monitor">
            <div className="tv-bezel">
              <div className="tv-screen">
                <iframe src={getYouTubeEmbedUrl(vendor.featuredVideoUrl)} title="Featured video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
              </div>
              <div className="tv-status-bar">
                <span className="tv-title">📹 Featured Video</span>
              </div>
            </div>
          </div>
          {vendor.recentVideos && vendor.recentVideos.length > 0 && (
            <div className="booth-side-panel">
              <h4 className="side-panel-title">Recent Videos</h4>
              <div className="side-panel-videos">
                {vendor.recentVideos.map((v, i) => (
                  <a href={`https://youtube.com/watch?v=${v.videoId}`} target="_blank" rel="noopener noreferrer" className="side-panel-card" key={i}>
                    <img src={v.thumbnail} alt={v.title} />
                    <span>{v.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!hasVideoAccess && (vendor.liveStreamId || vendor.featuredVideoUrl) && (
        <div style={{padding:'1rem', border:'1px solid rgba(200,164,92,0.2)', borderRadius:'var(--radius)', marginBottom:'1.5rem', textAlign:'center', background:'rgba(0,0,0,0.15)'}}>
          <p style={{margin:'0 0 0.5rem'}}>🎥 {vendor.liveStreamId ? 'This vendor is LIVE right now!' : 'This vendor has featured video content'}</p>
          <p style={{fontSize:'0.8rem', color:'var(--text-muted)', margin:'0 0 0.75rem'}}>Upgrade to Enthusiast or Insider to watch livestreams and featured videos.</p>
          <button className="btn-primary" onClick={() => handleUpgrade('enthusiast')} style={{fontSize:'0.85rem'}}>Unlock Video — $7.99/mo</button>
        </div>
      )}

      <div className="booth-header">
        <div className="booth-logo">
          {vendor.profileImageUrl ? (
            <img src={vendor.profileImageUrl} alt={vendor.businessName} />
          ) : (
            <div className="booth-logo-placeholder">🪵</div>
          )}
        </div>
        <div className="booth-info">
          <h1>{vendor.businessName}</h1>
          <p className="booth-specialty">{vendor.specialty}</p>
          <p className="booth-bio">{vendor.bio}</p>
          <BoothBadges vendorId={vendor.vendorId} />
        </div>
      </div>

      <div className="booth-catalog">
        {(() => {
          const regularItems = items.filter(i => i.itemType !== 'made_to_order');
          const mtoItems = items.filter(i => i.itemType === 'made_to_order');
          const hasMto = mtoItems.length > 0;

          return (
            <div className={`booth-layout ${hasMto ? 'split' : ''}`}>
              <div className="booth-regular">
                <h2>Available Works</h2>
                <div className="booth-items-grid">
                  {regularItems.map(item => (
                    <div className="booth-item-card" key={item.itemId} onClick={() => setLightboxItem(item)} style={{cursor:'pointer'}}>
                      <div className="booth-item-image">
                        {item.images && item.images.length > 0 ? (
                          <img src={item.images[0].url} alt={item.name} />
                        ) : null}
                      </div>
                      <h3>{item.name}</h3>
                      <ItemDescription text={item.description} />
                      {item.woodSpecies && item.woodSpecies.length > 0 && (
                        <p className="booth-item-species">{item.woodSpecies.join(', ')}</p>
                      )}
                      <p className="booth-item-price">${(item.price / 100).toFixed(2)}</p>
                      <span className="booth-item-type">{item.pricingType === 'bid' ? 'Bid Eligible' : item.pricingType === 'both' ? 'Buy or Bid' : 'Fixed Price'}</span>
                      {item.videoUrl && getYouTubeEmbedUrl(item.videoUrl) && (
                        <div className="item-video-embed">
                          <iframe src={getYouTubeEmbedUrl(item.videoUrl)} title={item.name} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {regularItems.length === 0 && !hasMto && <p className="empty-state">No items available yet.</p>}
              </div>

              {hasMto && (
                <div className="booth-mto">
                  <h2>🛠️ Made to Order</h2>
                  <div className="mto-items">
                    {mtoItems.map(item => (
                      <div className="mto-card" key={item.itemId} onClick={() => setLightboxItem(item)} style={{cursor:'pointer'}}>
                        <div className="mto-card-image">
                          {item.images && item.images.length > 0 ? (
                            <img src={item.images[0].url} alt={item.name} />
                          ) : null}
                        </div>
                        <div className="mto-card-info">
                          <span className="mto-badge">🛠️ Made to Order</span>
                          <h3>{item.name}</h3>
                          <p className="mto-subtext">Built just for you</p>
                          <p className="mto-time">Est. {item.processingDays || 14} days</p>
                          <p className="booth-item-price">${(item.price / 100).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {lightboxItem && (
        <div className="lightbox-overlay" onClick={() => setLightboxItem(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxItem(null)}>×</button>
            {lightboxItem.images && lightboxItem.images.length > 0 && (
              <div className="lightbox-images">
                {lightboxItem.images.map((img, idx) => (
                  <img key={idx} src={img.url} alt={lightboxItem.name} className="lightbox-img" />
                ))}
              </div>
            )}
            <div className="lightbox-details">
              <h2>{lightboxItem.name}</h2>
              <p className="lightbox-price">${(lightboxItem.price / 100).toFixed(2)}</p>
              {lightboxItem.description && <p className="lightbox-desc">{lightboxItem.description}</p>}
              {lightboxItem.woodSpecies && lightboxItem.woodSpecies.length > 0 && (
                <p className="lightbox-species">🪵 {lightboxItem.woodSpecies.join(', ')}</p>
              )}
              <span className="booth-item-type">{lightboxItem.pricingType === 'bid' ? 'Bid Eligible' : lightboxItem.pricingType === 'both' ? 'Buy or Bid' : 'Fixed Price'}</span>
              {customer && (lightboxItem.pricingType === 'fixed' || lightboxItem.pricingType === 'both') && (
                <LightboxActions item={lightboxItem} vendorId={vendor.vendorId} />
              )}
              {!customer && <p className="lightbox-login-hint">Sign in to purchase</p>}
            </div>
          </div>
        </div>
      )}

      <div className="booth-back">
        <Link to="/#vendors" className="btn-secondary">← Back to Expo Floor</Link>
      </div>
    </div>
  );
}

export default VendorBooth;
